import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Auth } from 'src/providers/auth/auth';
import { Events } from 'src/providers/events/events';
import { CreateAccountGuard } from 'src/providers/guard/create-account.guard';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';

import { DemoAccountService, DEMO_UNAVAILABLE_MESSAGE } from 'src/shared/services/demo-account.service';
import { FullScreenLoadingService } from 'src/shared/services/full-screen-loading.service';
import { PmDemoLandingService } from 'src/shared/services/pm-demo-landing.service';

import { Util } from 'src/shared/util/util';
import { CONNECTION_ERROR_MESSAGE, isConnectionError } from 'src/shared/util/connection-error';

@Component({
  template: '',
  standalone: false
})
export class CreateAccountRedirectComponent implements OnInit {

  private route = inject(ActivatedRoute);
  private auth = inject(Auth);
  private events = inject(Events);
  private navCtrl = inject(CustomNavController);
  private demoAccountService = inject(DemoAccountService);
  private fullScreenLoadingService = inject(FullScreenLoadingService);
  private pmDemoLanding = inject(PmDemoLandingService);
  private createAccountGuard = inject(CreateAccountGuard);
  private util = inject(Util);

  async ngOnInit() {
    if (this.auth.isAuthenticated()) {
      this.navCtrl.navigateRoot('properties');
      return;
    }

    // /create-account/rental mirrors picking "For Property Managers / Hosts" in the auth
    // modal: log into the shared demo account behind the "Preparing Demo" entrance cover
    // (recording the anonymous demo view) instead of creating a guest account.
    if (this.route.snapshot.params['accountType'] === 'rental') {
      await this.enterRentalDemo();
      return;
    }

    this.fullScreenLoadingService.show();
    try {
      const hkId = this.route.snapshot.params['hkId'];
      if (hkId) {
        const proName = this.route.snapshot.params['proName'];
        const proTeamName = this.route.snapshot.params['proTeamName'];
        localStorage.setItem('privateProHkId', hkId);
        localStorage.setItem('proName', proName ? decodeURI(proName) : '');
        localStorage.setItem('proTeamName', proTeamName ? decodeURI(proTeamName) : '');
      }
      await this.createAccountGuard.handleAccountCreation(this.route.snapshot);
    } finally {
      this.fullScreenLoadingService.hide();
    }
  }

  private async enterRentalDemo(): Promise<void> {
    // The "Preparing Demo" entrance cover is the loading state for this route. Drop the
    // create-account bowtie overlay (app.component shows it for /create-account/*) so it
    // never lingers behind the cover or reappears once the welcome card is dismissed.
    this.demoAccountService.beginEntrance();
    this.fullScreenLoadingService.hide();
    try {
      localStorage.setItem('isRentalClient', 'true');
      localStorage.setItem('guest_view_type', 'rental');
      this.events.publish('auth:type-selected');
      await this.demoAccountService.loginToDemoAccount('rental');
      // Enable the app shell: a fresh /create-account/rental load leaves enableMenu=false
      // (onboarding route, not yet authed) and the demo login doesn't publish 'menu:open'
      // like the guest flow does. The strategy route's ActivateMenuGuard publishes it too,
      // but do it here so the shell is up regardless of the destination's guards.
      this.events.publish('menu:open');
      // Property-manager demo landing page is A/B tested (pm-demo-landing-page),
      // same buckets as the auth-modal path. Control = Strategy/Income overview.
      const route = await this.pmDemoLanding.resolveLandingRoute();
      this.navCtrl.navigateRoot(route);
      this.demoAccountService.revealEntrance();
    } catch (err) {
      this.demoAccountService.cancelEntrance();
      if (isConnectionError(err)) {
        this.util.showErrorWithRetry(CONNECTION_ERROR_MESSAGE, () => this.enterRentalDemo());
      } else {
        this.util.showError(DEMO_UNAVAILABLE_MESSAGE);
      }
      console.error('Failed to log into demo account:', err);
      this.navCtrl.navigateRoot('properties');
    }
  }
}
